// Opening a suggestion of where to begin, from the panel's link or from a reply button under
// Mor's message: one route for both kinds, so both places do the same thing.

import { useState } from 'react';
import type { NavigateFunction } from 'react-router-dom';

import { ApiFailure, type ApiClient } from '../api/client.js';
import type { Suggestion } from '../api/contract.js';
import type { PlanState } from './usePlan.js';

export type OpenSuggestion = {
  open: (suggestion: Suggestion, options?: { from?: 'home' }) => Promise<void>;
  /** The id of the suggestion being opened, or null. */
  opening: string | null;
  problem: string | null;
};

export function useOpenSuggestion({
  client,
  planId,
  state,
  navigate,
}: {
  client: ApiClient;
  planId: string;
  state: PlanState;
  navigate: NavigateFunction;
}): OpenSuggestion {
  const [opening, setOpening] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  // The address changes before the snapshot is refetched: a topic that becomes started while
  // the reader is still on a plan page would otherwise take them to the topic.
  const open = async (
    suggestion: Suggestion,
    options?: { from?: 'home' },
  ): Promise<void> => {
    setProblem(null);
    if (suggestion.kind === 'issue' && suggestion.issue !== null) {
      navigate(`/plans/${planId}/topics/${suggestion.topic}/issues/${suggestion.issue}`);
      return;
    }
    setOpening(suggestion.id);
    try {
      const opened = await client.exploreSuggestion(planId, suggestion.id, options);
      navigate(
        opened.issue === null
          ? `/plans/${planId}/topics/${opened.topic}/issues`
          : `/plans/${planId}/topics/${opened.topic}/issues/${opened.issue}`,
      );
      state.refetch();
    } catch (error) {
      setProblem(
        error instanceof ApiFailure && error.code === 'refused'
          ? error.message
          : 'That didn’t open. Try again.',
      );
    } finally {
      setOpening(null);
    }
  };

  return { open, opening, problem };
}
